import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import useSWR from 'swr'
import { api, createTeam, ConflictError } from '../services/api'
import { Badge, Banner, Button, Panel, SlugManager, CompetitorSelect } from '../components'
import { Competitor } from '../types/forms'
import { deriveTeamName, deriveTeamAbbreviation } from '../utils/teamName'
import pageStyles from '../styles/page.module.scss'
import formStyles from '../styles/forms.module.scss'
import buttonStyles from '../components/Button.module.scss'

interface TeamPlayer {
  tc_competitor_id: string | null
  tc_name: string | null
  ext_name: string | null
  tc_short_name: string | null
  ext_short_name: string | null
}

interface Team {
  tc_team_id: string
  ext_competitor_id: string | null
  tc_name: string | null
  ext_name: string | null
  tc_abbreviation: string | null
  ext_abbreviation: string | null
  player1: TeamPlayer
  player2: TeamPlayer
  sport_name: string | null
  catalog_source_name: string | null
  effective_virtual: boolean
  created_at: string | null
  tc_updated_at: string | null
  ext_updated_at: string | null
}

interface TeamDetailResponse {
  team: Team
  timestamp: string
}

const TeamDetail: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()
  const navigate = useNavigate()
  const isCreateMode = teamId === 'new'

  const { data, isLoading, error, mutate } = useSWR(
    teamId && !isCreateMode ? ['team-detail', teamId] : null,
    () => api.get(`/teams/${teamId}`),
    { revalidateOnFocus: false }
  )

  const response: TeamDetailResponse | null = data?.body ? JSON.parse(data.body) : data
  const team = response?.team

  // ===========================================================================
  // Create mode
  // ===========================================================================

  const [player1, setPlayer1] = useState<Competitor | null>(null)
  const [player2, setPlayer2] = useState<Competitor | null>(null)
  const [tcName, setTcName] = useState('')
  const [tcAbbreviation, setTcAbbreviation] = useState('')
  const [tcNameDirty, setTcNameDirty] = useState(false)
  const [tcAbbrDirty, setTcAbbrDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [duplicateTeamId, setDuplicateTeamId] = useState<string | null>(null)

  // Auto-fill name and abbreviation when both players are picked, unless the
  // editor has manually edited the field. Re-runs whenever picks change.
  useEffect(() => {
    if (!isCreateMode) return
    if (!tcNameDirty) {
      setTcName(deriveTeamName(player1?.name, player2?.name))
    }
    if (!tcAbbrDirty) {
      setTcAbbreviation(
        deriveTeamAbbreviation(
          player1 ? { name: player1.name } : null,
          player2 ? { name: player2.name } : null,
        ),
      )
    }
  }, [player1, player2, tcNameDirty, tcAbbrDirty, isCreateMode])

  const canSubmit =
    !!player1 &&
    !!player2 &&
    player1.tc_id !== player2.tc_id &&
    tcName.trim().length > 0 &&
    !isSaving

  const handleCreate = async () => {
    if (!canSubmit || !player1 || !player2) return
    setIsSaving(true)
    setSaveError(null)
    setDuplicateTeamId(null)

    try {
      const result = await createTeam({
        player1_tc_competitor_id: player1.tc_id,
        player2_tc_competitor_id: player2.tc_id,
        tc_name: tcName.trim(),
        tc_abbreviation: tcAbbreviation.trim() || null,
      })
      const newId = result?.team?.tc_team_id
      if (newId) {
        navigate(`/teams/${newId}`, { replace: true })
      } else {
        setSaveError('Team created but no ID returned')
      }
    } catch (err: any) {
      if (err instanceof ConflictError) {
        const existing = err.data?.detail?.tc_team_id ?? null
        setDuplicateTeamId(existing)
        setSaveError(
          existing
            ? 'A team already exists for these two players.'
            : err.message || 'Conflict creating team',
        )
      } else {
        setSaveError(err.message || 'Failed to create team')
      }
    } finally {
      setIsSaving(false)
    }
  }

  if (isCreateMode) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.pageHeader}>
          <h1>Create Team</h1>
          <p>Build a doubles team from two existing singles players.</p>
        </div>

        {saveError && (
          <Banner
            variant="error"
            message={
              <span>
                {saveError}
                {duplicateTeamId && (
                  <>
                    {' '}
                    <Link to={`/teams/${duplicateTeamId}`}>View existing team</Link>
                  </>
                )}
              </span>
            }
            onDismiss={() => {
              setSaveError(null)
              setDuplicateTeamId(null)
            }}
          />
        )}

        <div className={formStyles.formContainer}>
          <div className={formStyles.formField}>
            <div className={formStyles.fieldGroup}>
              <label>Player 1 *</label>
              <CompetitorSelect
                value={player1}
                onChange={(c) => setPlayer1(c as Competitor | null)}
                teamType="singles"
                placeholder="Search for a player..."
                isDisabled={isSaving}
              />
            </div>

            <div className={formStyles.fieldGroup}>
              <label>Player 2 *</label>
              <CompetitorSelect
                value={player2}
                onChange={(c) => setPlayer2(c as Competitor | null)}
                teamType="singles"
                placeholder="Search for a player..."
                isDisabled={isSaving}
              />
              {player1 && player2 && player1.tc_id === player2.tc_id && (
                <p className={formStyles.errorText}>Player 2 must differ from Player 1.</p>
              )}
            </div>

            <div className={formStyles.fieldGroup}>
              <label>Team Name *</label>
              <input
                type="text"
                value={tcName}
                onChange={(e) => {
                  setTcName(e.target.value)
                  setTcNameDirty(true)
                }}
                placeholder="Auto-filled when both players are picked"
                disabled={isSaving}
              />
            </div>

            <div className={formStyles.fieldGroup}>
              <label>Abbreviation</label>
              <input
                type="text"
                value={tcAbbreviation}
                onChange={(e) => {
                  setTcAbbreviation(e.target.value)
                  setTcAbbrDirty(true)
                }}
                placeholder="Auto-filled when both players are picked"
                disabled={isSaving}
              />
            </div>
          </div>

          <div className={formStyles.formFooter}>
            <Link
              to="/teams"
              className={`${buttonStyles.button} ${buttonStyles.secondary} ${buttonStyles.medium}`}
            >
              Cancel
            </Link>
            <Button onClick={handleCreate} disabled={!canSubmit}>
              {isSaving ? 'Creating...' : 'Create Team'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // ===========================================================================
  // View mode (existing read-only behavior — Phase 2 will add edit affordances)
  // ===========================================================================

  const getDisplayName = (tcValue: string | null, extValue: string | null): string => {
    return tcValue || extValue || '—'
  }

  const getTeamName = (): string => {
    if (!team) return 'Unknown Team'
    return team.tc_name || team.ext_name || 'Unknown Team'
  }

  const getPlayerDisplayName = (player: TeamPlayer | null): string => {
    if (!player) return '—'
    return player.tc_name || player.ext_name || '—'
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading team</h2>
          <p>Failed to fetch team details: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  if (isLoading || !team) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading team details...</h3>
          <p>Please wait while we fetch the team data.</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className={pageStyles.pageHeader}>
        <h1>{getTeamName()}</h1>
        <p>
          {team.ext_competitor_id && (
            <code className="id-code">{team.ext_competitor_id}</code>
          )}
          {' '}
          {team.sport_name && <Badge variant="primary">{team.sport_name}</Badge>}
          {' '}
          {team.catalog_source_name && <Badge variant="secondary">{team.catalog_source_name}</Badge>}
          {' '}
          {team.effective_virtual && <Badge variant="warning">Placeholder</Badge>}
        </p>
      </div>

      <Panel>
        <Panel.Header>
          <h2>Team Information</h2>
        </Panel.Header>
        <div className={formStyles.fieldGroup}>
          <label>Team Name</label>
          <input type="text" value={getDisplayName(team.tc_name, team.ext_name)} disabled />
        </div>
        <div className={formStyles.fieldGroup}>
          <label>Abbreviation</label>
          <input type="text" value={getDisplayName(team.tc_abbreviation, team.ext_abbreviation)} disabled />
        </div>
        <div className={formStyles.fieldGroup}>
          <label>Player 1</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="text" value={getPlayerDisplayName(team.player1)} disabled style={{ flex: 1 }} />
            {team.player1?.tc_competitor_id && (
              <Link to={`/players/${team.player1.tc_competitor_id}`}>
                <Button size="small">View</Button>
              </Link>
            )}
          </div>
        </div>
        <div className={formStyles.fieldGroup}>
          <label>Player 2</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="text" value={getPlayerDisplayName(team.player2)} disabled style={{ flex: 1 }} />
            {team.player2?.tc_competitor_id && (
              <Link to={`/players/${team.player2.tc_competitor_id}`}>
                <Button size="small">View</Button>
              </Link>
            )}
          </div>
        </div>
        <div className={formStyles.fieldGroup}>
          <label>Sport</label>
          <input type="text" value={team.sport_name || '—'} disabled />
        </div>
        <div className={formStyles.fieldGroup}>
          <label>Data Source</label>
          <input type="text" value={team.catalog_source_name || '—'} disabled />
        </div>
      </Panel>

      <SlugManager entityType="team" entityId={team.tc_team_id} />
    </div>
  )
}

export default TeamDetail
